import type { ItemTypeKey } from './utils/MultiTypeAverageWindow';

export interface RenderStackEntry {
  recyclerKey: string;
  index: number;
  itemType: ItemTypeKey;
  stableId: string;
}

export interface RenderStackSyncArgs {
  engagedRange: { start: number; end: number } | null;
  itemCount: number;
  getStableId: (index: number) => string;
  getItemType: (index: number) => ItemTypeKey;
}

export interface RenderStackManagerOptions {
  maxItemsInRecyclePool?: number;
}

interface KeyMeta {
  index: number;
  itemType: ItemTypeKey;
  stableId: string;
}

export class RenderStackManager {
  private nextKeyId = 0;
  private readonly maxPoolSize: number;
  private readonly keyMap = new Map<string, KeyMeta>();
  private readonly stableIdMap = new Map<string, string>();
  private readonly pools = new Map<ItemTypeKey, Set<string>>();

  constructor(options: RenderStackManagerOptions = {}) {
    this.maxPoolSize = options.maxItemsInRecyclePool ?? Infinity;
  }

  getPoolSize(itemType: ItemTypeKey): number {
    return this.pools.get(itemType)?.size ?? 0;
  }

  sync(args: RenderStackSyncArgs): RenderStackEntry[] {
    const { engagedRange, itemCount, getStableId, getItemType } = args;

    if (engagedRange === null || itemCount === 0) {
      this.recycleAll();
      this.trimPools();
      return [];
    }

    // Snapshot the engaged set: stableId → { index, itemType }.
    // Insertion order matches index order, which the caller relies on.
    const engaged = new Map<string, { index: number; itemType: ItemTypeKey }>();
    const clampedEnd = Math.min(engagedRange.end, itemCount - 1);
    for (let index = engagedRange.start; index <= clampedEnd; index++) {
      engaged.set(getStableId(index), { index, itemType: getItemType(index) });
    }

    // Recycle any active key whose stableId left the engaged set or whose
    // itemType changed underneath it.
    for (const [recyclerKey, meta] of [...this.keyMap.entries()]) {
      const current = engaged.get(meta.stableId);
      if (!current || current.itemType !== meta.itemType) {
        this.recycleKey(recyclerKey);
      }
    }

    // Assign a recycler key to each engaged stableId, preferring an existing
    // key, then the type-specific pool, then a fresh mint.
    const stack: RenderStackEntry[] = [];
    for (const [stableId, { index, itemType }] of engaged) {
      let recyclerKey = this.stableIdMap.get(stableId);
      if (recyclerKey == null) {
        recyclerKey = this.takeFromPool(itemType) ?? this.mintKey();
      }
      this.keyMap.set(recyclerKey, { index, itemType, stableId });
      this.stableIdMap.set(stableId, recyclerKey);
      stack.push({ recyclerKey, index, itemType, stableId });
    }

    this.trimPools();
    return stack;
  }

  private recycleKey(recyclerKey: string): void {
    const meta = this.keyMap.get(recyclerKey);
    if (!meta) return;
    this.keyMap.delete(recyclerKey);
    this.stableIdMap.delete(meta.stableId);
    let pool = this.pools.get(meta.itemType);
    if (!pool) {
      pool = new Set<string>();
      this.pools.set(meta.itemType, pool);
    }
    pool.add(recyclerKey);
  }

  private recycleAll(): void {
    for (const recyclerKey of [...this.keyMap.keys()]) {
      this.recycleKey(recyclerKey);
    }
  }

  private takeFromPool(itemType: ItemTypeKey): string | undefined {
    const pool = this.pools.get(itemType);
    if (!pool || pool.size === 0) return undefined;
    const oldest = pool.values().next().value as string;
    pool.delete(oldest);
    return oldest;
  }

  private mintKey(): string {
    return `cell_${this.nextKeyId++}`;
  }

  private trimPools(): void {
    if (!Number.isFinite(this.maxPoolSize)) return;
    for (const pool of this.pools.values()) {
      while (pool.size > this.maxPoolSize) {
        const oldest = pool.values().next().value as string;
        pool.delete(oldest);
      }
    }
  }
}
